import { UnlockButtons } from '../LockedModule';
import { T } from './theme';
import CoursesHero from './CoursesHero';
import LessonSearch from './LessonSearch';
import FeaturedCourse, { CourseRow } from './FeaturedCourse';
import { PRO_URL } from '../../config/links';
import { openLanding, PLUSZ_PATH } from '../../lib/openLanding';
import { track } from '../../lib/track';
import { useIsMobile } from '../../hooks/useIsMobile';
import { KS_LESSONS } from '../../config/leckeszam';
import { AccountExtras, WelcomeCard } from './AccountExtras';

// Presentational: everything on /tanfolyamok below the navbar. CoursesPage
// owns the data; this only renders it, so it can also be shown with fixture
// data in development.
const CoursesView = ({
  loading, tier, ksReadable, session, name, showOg, access, discordOsszekotve,
  featured, others, locked, purchasable, proLessonCount,
  search, onStart, onOverview, onPurchase,
}) => {
  const isMobile = useIsMobile();
  const returning = !!featured?.outline?.hasProgress;

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: isMobile ? '28px 16px 56px' : '48px 32px 80px' }}>
      <CoursesHero name={name} tier={tier} ksReadable={ksReadable} showOg={showOg} returning={returning} isMobile={isMobile}>
        <LessonSearch {...search} isMobile={isMobile} />
      </CoursesHero>

      {/* Navigáció 1: üdvözlő kártya az első (legfeljebb három) egyetemi látogatáskor */}
      {!loading && <WelcomeCard access={access} session={session} isMobile={isMobile} discordOsszekotve={discordOsszekotve} />}

      {loading || (!featured && !locked) ? (
        <div style={{ color: T.faint, fontSize: 14, padding: '24px 0' }}>Betöltés…</div>
      ) : featured && (
        <FeaturedCourse
          course={featured.course}
          outline={featured.outline}
          onStart={onStart}
          onOverview={onOverview}
          isMobile={isMobile}
        />
      )}

      {/* Navigáció 1: telefonon az első tanfolyamkártya és a „Folytasd” sor után */}
      {isMobile && !loading && <AccountExtras access={access} session={session} isMobile discordOsszekotve={discordOsszekotve} />}

      {others.length > 0 && (
        <section style={{ marginTop: 40 }}>
          <h2 style={{ color: T.text, fontSize: 18, fontWeight: 700, margin: '0 0 14px' }}>További tanfolyamaid</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {others.map(o => (
              <CourseRow key={o.course.id} course={o.course} outline={o.outline} onStart={onStart} isMobile={isMobile} />
            ))}
          </div>
        </section>
      )}

      {/* asztalon a tanfolyamlista alatt, hogy a tanfolyamokat ne tolja le */}
      {!isMobile && !loading && <AccountExtras access={access} session={session} isMobile={false} discordOsszekotve={discordOsszekotve} />}

      {!loading && locked && (
        <div style={{ marginTop: featured ? 40 : 0 }}>
          <FeaturedCourse course={locked.course} outline={locked.outline} locked isMobile={isMobile} />
        </div>
      )}

      {purchasable.length > 0 && (
        <section style={{ marginTop: 48 }}>
          <h2 style={{ color: T.text, fontSize: 18, fontWeight: 700, margin: '0 0 14px' }}>Plusz-kurzusok</h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {purchasable.map(course => (
              <div key={course.id} style={{
                background: T.surface, border: `1px solid ${T.line}`, borderRadius: 16, padding: 22,
                display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <h3 style={{ color: T.text, fontSize: 17, fontWeight: 700, margin: 0 }}><span aria-label="Zárt kurzus">🔒 </span>{course.title}</h3>
                {course.description && (
                  <p style={{ color: T.muted, fontSize: 13, lineHeight: 1.6, margin: 0, flex: 1 }}>{course.description}</p>
                )}
                {/* Plusz-szintű kurzus (Plusz 2. kör): Plusszal nyílik, vagy külön megvásárolható */}
                <UnlockButtons level="plusz" session={session} compact />
                <button
                  type="button"
                  onClick={() => onPurchase(course.slug)}
                  style={{
                    marginTop: 6, alignSelf: 'flex-start',
                    background: 'none', border: `1px solid ${T.accentLine}`, color: T.accent,
                    borderRadius: 10, padding: '10px 16px', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  }}
                >
                  vagy megvásárlom{course.price ? ` · ${course.price}\u00a0€` : ''} →
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Plusz 3. kör: FREE és TAG felé a Plusz a fő ajánlat, a PRO kisebb linkként. */}
      {(tier === 'free' || tier === 'tag') && (
        <section style={{
          marginTop: 48, padding: isMobile ? '22px 20px' : '26px 30px',
          background: T.surface, border: `1px solid ${T.accent}55`, borderRadius: 16,
          boxShadow: '0 0 40px -16px rgba(0,210,200,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 18,
        }}>
          <div style={{ minWidth: 0, flex: '1 1 260px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ color: T.text, fontWeight: 700, fontSize: 17 }}>Plusz</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.accent, border: `1px solid ${T.accent}66`, borderRadius: 100, padding: '2px 8px' }}>Ajánlott</span>
            </div>
            <div style={{ color: T.muted, fontSize: 14, lineHeight: 1.55 }}>
              A teljes Kripto Start tanfolyam (mind a {KS_LESSONS} lecke) és a Kripto Terminál bővebb keretei, egy előfizetésben. Havi 12{'\u00a0'}€, vagy éves díjjal 8,25{'\u00a0'}€/hó.
            </div>
          </div>
          <div style={{ display: 'grid', gap: 8, justifyItems: isMobile ? 'start' : 'end' }}>
            <button
              type="button"
              onClick={() => { track(session, 'plusz_cta_click', { hely: 'egyetem_kurzuslista' }); openLanding(PLUSZ_PATH); }}
              style={{ border: 'none', borderRadius: 10, padding: '11px 16px', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', background: T.accent, color: '#0a0f1e', whiteSpace: 'nowrap' }}
            >
              Plusz-előfizetés →
            </button>
            <a href={PRO_URL} target="_blank" rel="noopener noreferrer" style={{ color: T.muted, fontSize: 13, textDecoration: 'underline' }}>
              Mit tud a PRO?
            </a>
          </div>
        </section>
      )}

      {tier === 'plusz' && (
        <section style={{
          marginTop: 48, padding: isMobile ? '22px 20px' : '26px 30px',
          background: T.surface, border: `1px solid ${T.line}`, borderRadius: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 18,
        }}>
          <div style={{ minWidth: 0, flex: '1 1 260px' }}>
            <div style={{ color: T.text, fontWeight: 700, fontSize: 17, marginBottom: 6 }}>PRO tagság</div>
            <div style={{ color: T.muted, fontSize: 14, lineHeight: 1.55 }}>
              {proLessonCount > 0 ? `${proLessonCount} exkluzív lecke, ` : ''}Kripto Terminál PRO funkciók, saját indikátorok, heti PRO elemzések.
            </div>
          </div>
          <a
            href={PRO_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: T.accent, fontWeight: 700, fontSize: 14, textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            PRO részletei →
          </a>
        </section>
      )}
    </div>
  );
};

export default CoursesView;
